import { createTheme } from '@mui/material/styles';
import config from './config';
import MontserratTtf from '../styles/fonts/Montserrat-VariableFont_wght.ttf';

export const baseFonts = [
  'Montserrat',
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
];

const customButtonVariants = [
  'primary',
  'primaryGradient',
  'secondary',
  'secondaryGradient',
  'primaryOutline',
];

const colors = config().colors;
// https://m2.material.io/design/color/the-color-system.html#tools-for-picking-colors
// const brandColor = {
//   '50': tinycolor(primaryColor),
//   '100': tinycolor(primaryColor),
//   '200': tinycolor(primaryColor),
//   '300': tinycolor(primaryColor),
//   '400': tinycolor(primaryColor),
//   '500': primaryColor,
//   700: "#0288d1"
//   800: "#0277bd"
//   900: "#01579b"
//   A100: "#80d8ff"
//   A200: "#40c4ff"
//   A400: "#00b0ff"
//   A700: "#0091ea"
// };

export const breakpoints = {
  xs: 0,
  xsm: 480,
  sm: 768,
  md: 900,
  lg: 1200,
  xl: 1536,
};

export const MAX_WITH_MOBILE_MEDIA = `@media (max-width: ${breakpoints.sm}px)`;

const defaultThemeConfig = {
  breakpoints: {
    values: breakpoints,
  },
  palette: {
    primary: {
      // ..lightBlue,
      main: colors.primary,
      contrastText: '#ffffff'
    },
    secondary: {
      main: colors.secondary,
      contrastText: '#ffffff',
    },
    success: {
      main: colors.success,
      contrastText: '#ffffff',
    },
    warning: {
      main: colors.warning,
      contrastText: '#ffffff',
    },
    dark: {
      main: colors.dark,
      contrastText: '#ffffff',
    },
    // secondary: {
    //   main: colors.secondary,
    // },
    info: {
      main: colors.info,
      // contrastText: colors.primary,
    },
    light: {
      main: colors.light,
      contrastText: 'black',
    },
    purple: {
      main: colors.purple,
    },
    teal: {
      main: colors.teal,
    },
    lightTeal: {
      main: colors.lightTeal,
    },
    text: {
      primary: colors.text,
      secondary: colors.secondaryText,
      light: colors.lightText,
      muted: colors.muted,
      black: colors.blackText,
      orange: colors.orange,
      blue: colors.primary,
      gray: colors.gray,
      danger: colors.error
    },
    // success: {
    //   main: colors.success,
    // },
    // error: {
    //   main: colors.danger,
    // },
    // warning: {
    //   main: colors.warning,
    // },
  },
  typography: {
    fontFamily: baseFonts.join(','),
    fontSize: 16,
    h1: {
      fontSize: '1.59375rem',
      fontWeight: 'bold',
      fontFamily: [config().brandFont, ...baseFonts].join(','),
    },
    h1Inter: {
      fontSize: '1.59375rem',
      fontWeight: 'bold',
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    h2: {
      fontSize: '1.125rem',
      fontWeight: 600,
      fontFamily: [config().brandFont, ...baseFonts].join(','),
    },
    h2Inter: {
      fontSize: '1.125rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    h3: {
      fontSize: '0.9rem',
      fontWeight: 600,
      fontFamily: [config().brandFont, ...baseFonts].join(','),
    },
    h3Inter: {
      fontSize: '0.9rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    h4: {
      fontSize: '0.7125rem',
      fontWeight: 600,
      fontFamily: [config().brandFont, ...baseFonts].join(','),
    },
    h4Inter: {
      fontSize: '0.7125rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    h5: {
      fontSize: '0.675rem',
      fontWeight: 600,
      fontFamily: [config().brandFont, ...baseFonts].join(','),
    },
    h5Inter: {
      fontSize: '0.675rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    h6: {
      fontSize: '0.6375rem',
      fontWeight: 600,
      fontFamily: [config().brandFont, ...baseFonts].join(','),
    },
    h6Inter: {
      fontSize: '0.6375rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    title2: {
      fontSize: '2rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
    },
    title4: {
      fontSize: '1.5rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.5rem',
    },
    title4Inter: {
      fontSize: '1.5rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.375rem',
      letterSpacing: '-0.03125rem',
    },
    body: {
      fontSize: '0.9rem',
    },
    body1: {
      fontSize: '0.825rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    body2: {
      fontSize: '0.675rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
    },
    bodySmall: {
      fontSize: '0.516rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    bodyLarge: {
      fontSize: '1.1rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    strong: {
      fontSize: '0.825rem',
      fontWeight: 700,
      fontFamily: [...baseFonts].join(','),
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
      lineHeight: 'normal',
      letterSpacing: '1.2px',
      color: colors.text,
      [`@media (max-width: ${breakpoints.xsm}px)`]: {
        fontSize: '1.125rem',
      }
    },
    subtitle1: {
      fontWeight: 'bold',
      color: 'rgb(30,30,30)',
      fontFamily: [...baseFonts].join(','),
      // color: 'rgb(100,100,100)'
      // fontFamily: ['GT America', ...baseFonts].join(','),
    },
    subtitle2: {
      fontSize: '1.03125rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
      lineHeight: 'normal',
      color: colors.text,
    },
    subtitle2Inter: {
      fontSize: '1.03125rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: 'normal',
      color: colors.text,
    },
    paragraphMedium: {
      fontSize: '1rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
      lineHeight: 'normal',
    },
    paragraphMediumInter: {
      fontSize: '1rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: 'normal',
    },
    paragraphMedium500: {
      fontSize: '1rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
      lineHeight: 'normal',
    },
    paragraphMedium500Inter: {
      fontSize: '1rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: 'normal',
    },
    paragraphSmall: {
      fontSize: '0.875rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.25rem',
    },
    paragraphSmallInter: {
      fontSize: '0.875rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: 'normal',
    },
    paragraphSmall500: {
      fontSize: '0.875rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.25rem',
    },
    paragraphSmall500Inter: {
      fontSize: '0.875rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: 'normal',
    },
    button: {
      textTransform: 'none',
      fontFamily: [...baseFonts].join(','),
    },
    formLabel: {
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: [...baseFonts].join(','),
    },
    helperText: {
      fontSize: '0.675rem',
      fontWeight: 400,
      color: 'rgba(0,0,0,0.6)',
      fontFamily: [...baseFonts].join(','),
    },
    xSmall: {
      fontSize: '0.4921875rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    small: {
      fontSize: '0.65625rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    smallInter: {
      fontSize: '0.65625rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    small500: {
      fontSize: '0.65625rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
    },
    small500xTight: {
      fontSize: '0.65625rem',
      fontWeight: 500,
      lineHeight: '0.75rem',
      fontFamily: [...baseFonts].join(','),
    },
    small500Inter: {
      fontSize: '0.65625rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.059rem',
    },
    small600: {
      fontSize: '0.65625rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
    },
    normal: {
      fontSize: '0.75rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    normalInter: {
      fontSize: '0.75rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.375rem',
    },
    normal500: {
      fontSize: '0.75rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
      lineHeight: 'normal',
      letterSpacing: '0.018rem'
    },
    normal500TightInter: {
      fontSize: '0.75rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.375rem',
      letterSpacing: '-0.4px'
    },
    normal600: {
      fontSize: '0.75rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
    },
    normal600Inter: {
      fontSize: '0.75rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.375rem',
    },
    large: {
      fontSize: '0.84375rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    largeInter: {
      fontSize: '0.84375rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    extraLarge: {
      fontSize: '1.125rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
    },
    extraLargeInter: {
      fontSize: '1.125rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    extraLarge500: {
      fontSize: '1.125rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
    },
    extraLarge500Inter: {
      fontSize: '1.125rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    large300Loose: {
      fontSize: '0.84375rem',
      fontWeight: 300,
      lineHeight: '1.406rem',
      fontFamily: [...baseFonts].join(','),
    },
    large500Tight: {
      fontSize: '0.84375rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.021rem',
      color: colors.text,
    },
    large500TightInter: {
      fontSize: '0.84375rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.029rem',
    },
    large600Tight: {
      fontSize: '0.84375rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.029rem',
      color: colors.text,
    },
    large600TightInter: {
      fontSize: '0.84375rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.029rem',
    },
    largeSemiBold: {
      fontSize: '0.84375rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
      color: colors.text,
    },
    largeSemiBoldTight: {
      fontSize: '0.84375rem',
      fontWeight: 600,
      lineHeight: '1.029rem',
      fontFamily: [...baseFonts].join(','),
      color: colors.text,
    },
    largeSemiBoldLoose: {
      fontSize: '0.84375rem',
      fontWeight: 600,
      lineHeight: '1.875rem',
      fontFamily: [...baseFonts].join(','),
      color: colors.text,
    },
    largeSemiBoldLooseInter: {
      fontSize: '0.84375rem',
      fontWeight: 600,
      lineHeight: '1.875rem',
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      color: colors.text,
    },
    largeBold: {
      fontSize: '0.84375rem',
      fontWeight: 700,
      fontFamily: [...baseFonts].join(','),
      color: colors.text,
    },
    largeBoldLoose: {
      fontSize: '0.84375rem',
      fontWeight: 700,
      lineHeight: '1.406rem',
      fontFamily: [...baseFonts].join(','),
      color: colors.text,
    },
    largeBoldLooseInter: {
      fontSize: '0.84375rem',
      fontWeight: 700,
      lineHeight: '1.875rem',
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      color: colors.text,
    },
    // caption: {
    //   fontSize: '0.8em',
    // },
    // overline: {
    //   fontSize: '',
    // },
    slightlyLarge500Inter: {
      fontSize: '0.84375rem',
      fontWeight: 500,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    xSmallSemiLoose: {
      fontSize: '0.5625rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.375rem',
      letterSpacing: '-0.03125rem',
    },
    xSmallSemiLooseInter: {
      fontSize: '0.5625rem',
      fontWeight: 400,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.375rem',
      letterSpacing: '-0.03125rem',
    },
    xSmallSemi500Loose: {
      fontSize: '0.5625rem',
      fontWeight: 500,
      fontFamily: [...baseFonts].join(','),
    },
    xSmallSemiBoldLoose: {
      fontSize: '0.5625rem',
      fontWeight: 600,
      fontFamily: [...baseFonts].join(','),
    },
    xSmallSemiBoldLooseInter: {
      fontSize: '0.5625rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.375rem',
      color: config().colors.lightText,
    },
    smallSemiBoldTightInter: {
      fontSize: '0.65625rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
      lineHeight: '1.059rem',
    },
    normalSemiBoldInter: {
      fontSize: '0.75rem',
      fontWeight: 600,
      fontFamily: [config().secondFont, ...baseFonts].join(','),
    },
    pageTitleResponsive: {
      fontSize: '1.125rem',
      fontWeight: 400,
      fontFamily: [...baseFonts].join(','),
      lineHeight: '1.875rem',
      colors: config().colors.text,
      [`@media (max-width: ${breakpoints.xsm}px)`]: {
        fontSize: '0.75rem',
        lineHeight: '1.5rem',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
      }
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Montserrat';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Montserrat'), local('Montserrat-Regular'), url(${MontserratTtf}) format('ttf');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: [...baseFonts].join(','),
          fontSize: '0.75rem',
        },
        contained: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '12px',
          fontFamily: [...baseFonts].join(','),
          fontSize: '0.75rem',
          alignItems: 'center',
          lineHeight: 'normal',
          fontStyle: 'normal',
          letterSpacing: '0.6px',
        },
        outlined: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '12px',
          fontFamily: [...baseFonts].join(','),
          fontSize: '0.75rem',
          alignItems: 'center',
          lineHeight: 'normal',
          fontStyle: 'normal',
          letterSpacing: '0.6px',
        }
      },
      variants: [
        {
          props: { variant: 'primary' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.silver}`,
            color: 'white',
            fontSize: '0.9rem',
            fontWeight: 400,
            lineHeight: 'normal',
            fontStyle: 'normal',
            letterSpacing: '0.6px',
            fontFamily: [config().secondFont, ...baseFonts].join(','),
            background: colors.primary,
            '&:hover': {
              background: colors.primary,
              opacity: 0.8,
            },
          },
        },
        // {
        //   props: { variant: 'contained' },
        //   style: {
        //     display: 'inline-flex',
        //     alignItems: 'center',
        //     borderRadius: '12px',
        //     textTransform: 'none',
        //     fontWeight: 600,
        //     fontFamily: [...baseFonts].join(','),
        //     fontSize: '0.75rem',
        //     alignItems: 'center',
        //     lineHeight: 'normal',
        //     fontStyle: 'normal',
        //     letterSpacing: '0.6px',
        //   }
        // },
        {
          props: { variant: 'outlined' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.primary}`,
            color: colors.primary,
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 'normal',
            fontStyle: 'normal',
            letterSpacing: '0.6px',
            fontFamily: [config().secondFont, ...baseFonts].join(','),
            // background: colors.primary,
            // '&:hover': {
            //   background: colors.primary,
            //   opacity: 0.8,
            // },
          },
        },
        {
          props: { variant: 'gradient' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.silver}`,
            color: 'white',
            fontSize: '0.9rem',
            fontWeight: 400,
            lineHeight: 'normal',
            fontStyle: 'normal',
            letterSpacing: '0.6px',
            fontFamily: [config().secondFont, ...baseFonts].join(','),
            background: colors.primary,
            '&:hover': {
              background: colors.primary,
              opacity: 0.8,
            },
          },
        },
        {
          props: { variant: 'primaryGradient' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.silver}`,
            color: 'white',
            fontSize: '0.9rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            letterSpacing: '0.6px',
            background: 'linear-gradient(88deg,#4ac4ff 3.82%,#0ba9f5 87.85%)',
            '&:hover': {
              background: 'linear-gradient(88deg,#4ac4ff 3.82%,#0ba9f5 87.85%)',
              opacity: 0.8,
            },
          },
        },
        {
          props: { variant: 'gray' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.silver}`,
            color: 'white',
            fontSize: '0.9rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            letterSpacing: '0.6px',
            background: 'rgba(0, 0, 0, 0.12)',
          },
        },
        {
          props: { variant: 'secondary' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.silver}`,
            color: 'white',
            fontSize: '0.9rem',
            fontWeight: 400,
            lineHeight: 'normal',
            fontStyle: 'normal',
            letterSpacing: '0.6px',
            background: colors.secondary,
            '&:hover': {
              background: colors.secondary,
              opacity: 0.8,
            },
          },
        },
        {
          props: { variant: 'secondaryGradient' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${colors.silver}`,
            color: 'white',
            fontSize: '0.9rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            letterSpacing: '0.6px',
            background: 'linear-gradient(80deg, #FF8838 18.56%, #FF6700 69.69%)',
            '&:hover': {
              background: 'linear-gradient(80deg, #FF8838 18.56%, #FF6700 69.69%)',
              opacity: 0.8,
            },
          },
        },
        {
          props: { variant: 'primaryOutline' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            fontSize: '0.9rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            letterSpacing: '0.6px',
            border: `1px solid ${colors.primary}`,
            color: colors.primary,
            background: 'white',
            fill: colors.primary,
            '&:hover': {
              opacity: 0.8,
            },
            '& svg': {
              path: {
                fill: colors.primary
              }
            },
          },
        },
        {
          props: { variant: 'backButton' },
          style: {
            color: config().colors.primary,
            width: 'fit-content',
            fontSize: '0.65625rem',
            fontWeight: 600,
            lineHeight: '1.25rem',
            gap: '2px'
          },
        },
        {
          props: { variant: 'grayOutline' },
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '12px',
            background: colors.white,
            border: `1px solid ${colors.border}`,
            [MAX_WITH_MOBILE_MEDIA]: {
              padding: '0.5rem 0.75rem',
              fontSize: '0.75rem',
            },
          },
        },
        {
          props: { color: 'dark' },
          style: {
            background: colors.dark,
            color: '#ffffff',
            '&:hover': {
              background: colors.dark,
              opacity: 0.9,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: '10px',
          boxShadow: 'none',
          ...(ownerState.size === 'xlarge' && customButtonVariants.includes(ownerState.variant) && {
            padding: '0.65rem 1rem 0.65rem 1rem',
            fontSize: '1.375rem',
            '& svg': {
              width: '1.875rem',
              height: '1.875rem',
            },
          }),
          ...(ownerState.size === 'large' && customButtonVariants.includes(ownerState.variant) && {
            padding: '8px 10px',
            fontSize: '1.03125rem',
            '& svg': {
              width: '1.40625rem',
              height: '1.40625rem',
            },
          }),
          ...(ownerState.size === 'xmedium' && customButtonVariants.includes(ownerState.variant) && {
            padding: '0.4rem 0.8rem',
            fontSize: '1rem',
            '& svg': {
              width: '1.3125rem',
              height: '1.3125rem',
            },
          }),
          ...(ownerState.size === 'medium' && customButtonVariants.includes(ownerState.variant) && {
            padding: '0.4rem 0.8rem',
            fontSize: '0.75rem',
            '& svg': {
              width: '1.313rem',
              height: '1.313rem',
            },
          }),
          ...(ownerState.size === 'small' && customButtonVariants.includes(ownerState.variant) && {
            padding: '4px 16px',
            fontSize: '0.65625rem',
            '& svg': {
              width: '1rem',
              height: '1rem',
            },
          }),
          ...(ownerState.disabled && {
            color: 'rgba(0, 0, 0, 0.26) !important',
            background: 'rgba(0, 0, 0, 0.09) !important',
            borderColor: 'transparent !important',
          }),

        }),
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: '10px',
          boxShadow: 'none',
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: () => ({
          fontSize: '0.75rem',
          // padding: 0,
        }),
        input: () => ({
          // padding: 12,
        })
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: () => ({
          borderRadius: '12px',
          '& .MuiOutlinedInput-root': {
            paddingRight: '24px !important'
          },
          '& .MuiAutocomplete-endAdornment': {
            top: 'calc(50% - 10px)',
          },
        }),
        inputRoot: ({ ownerState }) => ({
          padding: '0.75px 9px',
          ...(ownerState.noBorder === 'true' && {
            '& fieldset': {
              border: 'none',
            }
          }),
        }),
        clearIndicator: ({ ownerState }) => ({
          '& svg': {
            width: '0.75rem',
            height: '0.44056rem',
            fill: ownerState.colorIcon ? ownerState.colorIcon : colors.grayLight,
          },
        }),
        popupIndicator: ({ ownerState }) => ({
          '& svg': {
            width: '0.75rem',
            height: '0.44056rem',
            fill: ownerState.colorIcon ? ownerState.colorIcon : colors.grayLight,
          },
        }),
        input: ({ ownerState }) => ({
          fontSize: '0.75rem',
          ...(ownerState.textAlign && {
            textAlign: ownerState.textAlign,
          }),
          ...(ownerState.colorText && {
            color: ownerState.colorText,
          }),
        }),
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: () => ({
          backgroundColor: colors.white,
          borderRadius: config().styles.defaultRadius,
          '& .MuiInputBase-input': {
            color: colors.text,
            fontFamily: 'Inter',
            fontSize: '0.84375rem',
            fontWeight: 300,
            lineHeight: '1.406rem',
            padding: '7.18px 14px',
          },
          '& .MuiInputBase-root.Mui-disabled': {
            background: 'rgba(0, 0, 0, 0.04) !important',
          },
          '& .MuiInputBase-multiline': {
            padding: 0,
          },
          '&.custom-text-field-rounded': {
            '.MuiOutlinedInput-notchedOutline': {
              borderRadius: '12px'
            }
          }
        })
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.boxShadow && {
            borderRadius: '0.75rem',
            border: '0.5px solid #D7DEE1',
            boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.10)',
            '&.Mui-expanded': {
              boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.10)',
            },
          }),

        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: () => ({
          fontWeight: 700,
        }),
        primary: () => ({
          fontWeight: 700,
        })
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: () => ({
          color: config().colors.lightText,
          '&.Mui-checked': {
            color: config().colors.primary,
          },
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: () => ({
          'th': {
            fontSize: '0.65625rem',
            fontWeight: 500,
            paddingBottom: '0.125rem'
          }
        })
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: () => ({
          'td': {
            height: '2.5rem',
            padding: '0.4rem'
          },
        })
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: () => ({
          fontSize: '0.65625rem',
          fontWeight: 400,
          color: config().colors.text,
          fontFamily: [config().secondFont, ...baseFonts].join(','),
        })
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          [MAX_WITH_MOBILE_MEDIA]: {
            margin: '0.625rem',
            width: 'calc(100% - 20px)',
            maxHeight: 'calc(100% - 20px)',
          },
        },
      }
    }
    //   MuiListItemIcon: {
    //   styleOverrides: {
    //     root: {
    //       fontSize: '0.8rem',
    //     }
    //   }
    // },
    // MuiListItemText: {
    //   styleOverrides: {
    //     root: {
    //       fontSize: '0.8rem',
    //     }
    //   }
    // },
  },
  // status: {
  //   danger: ,
  // },
};

const theme = (themeConfig = defaultThemeConfig) => createTheme(themeConfig);

export default theme;
